import {
  BucketSummaryEnum,
  BucketTwrFormula,
  ReportJobStatus,
  SingleValueCardColor,
  SingleValueCardType,
} from "constants/index";

export type ColumnPeriodT = {
  name: string;
  period: [string, string];
};

export interface PerformanceComparisonTableProps {
  dateRange: [string | null, string | null];
  onColumnPeriodsChange: (periods: ColumnPeriodT[]) => void;
}

export interface PerformanceColumnSelectorModalProps {
  open: boolean;
  onClose: () => void;
  columnPeriods: ColumnPeriodT[];
  onChange: (columns: ColumnPeriodT[]) => void;
  dateRange: [string | null, string | null];
}

export interface StatisticState {
  dateRange: [string | null, string | null];
}

export interface IncomeSummaryProps {
  columnPeriods: ColumnPeriodT[];
}

export interface OtherStatisticProps {
  dateRange: [string | null, string | null];
  columnPeriods: ColumnPeriodT[];
}

// Report related interfaces
export interface ReportValue {
  date: string;
  beginning_value: number;
  value_change: number;
  cashflows: number;
  dividends: number;
  closing_price: number;
  nav_price: number;
  unit_balance: number;
  factor: number;
  stock_factor: number;
  time_weighted_return: number;
  time_weighted_return_stock: number;
}

export interface IncomeSummaryItem {
  name: string;
  value: ReportValue;
}

export interface GraphData {
  date: string;
  stock_value: number;
  option_value: number;
}

export interface ReportResponse {
  status: string;
  data: {
    report_data: string;
    income_summary: IncomeSummaryItem[];
    graph_data: GraphData[];
  };
  message: string;
}

export interface ReportParams {
  account_ids: number[];
  strategy_ids: number[];
  periods: {
    name: string;
    period: [string, string];
  }[];
}

export interface NewReportRequest {
  orion_account_id: number;
  asset_id: number;
  option_type: OptionTypeEnum;
  start_date: string;
  end_date: string;
  global_cumulative_period?: [string, string];
  report_type: ReportType;
  exclude_transactions?: OrionTransaction["id"][];
}

/**
 * Batch STRATEGY report over an account's persisted Bucket 1/2 members.
 * Buckets are CP-only — each pair runs one combined call+put report — so
 * no option_type is sent. This phase returns only the combined excel (one
 * sheet per pair) + per-pair statuses.
 */
export interface BucketReportRequest {
  orion_account_id: number;
  start_date: string;
  end_date: string;
  // Pair buckets (1/2) of orion_account_id to include.
  buckets?: number[];
  // The exact Bucket 3 to add as the account-level Bucket3 sheet — may
  // belong to a different account; the backend derives it from the bucket.
  bucket_3_id?: number;
  // Tax percentages from the report settings: the federal rate is a
  // browser-local setting, the state rate comes from the client's TaxRate
  // mapping (null when no state is selected). They drive the Keep card's
  // tax savings.
  federal_tax_rate?: number;
  state_tax_rate?: number | null;
  // Which time-weighted return to calculate. Optional, and absent means
  // DAILY_WEIGHT_ADJUSTMENT, the default the backend coerces to -- which is
  // also where a stored "weights_as_of" lands, that formula having been
  // retired with its arithmetic in task 3603. The Summary gear's pick is per
  // session and is never persisted.
  twr_formula?: BucketTwrFormula;
  // The option-pairing fix (task 3869): true pairs each option by its own
  // letter (CALL sold to open / bought to cover, PUT bought to open / sold to
  // close), so a bought PUT still open or held to expiry is valued. Absent
  // means true; false asks for the old formula. Per session, never persisted.
  option_pairing_v2?: boolean;
}

export enum BucketReportStatusEnum {
  SUCCESS = "success",
  ERROR = "error",
  // A Bucket 1/2 member the account did not hold in the report's period:
  // listed, never calculated, and deliberately not a failure (task 3792).
  NOT_HELD = "not_held",
}

export interface BucketPairReportStatus {
  bucket: number;
  // null = the account-level Bucket 3 sheet (one job for the whole bucket,
  // no single pair).
  symbol: string | null;
  // null = a portfolio-only report (the pair has no option history, which
  // is expected — it still gets its sheet, on portfolio values alone), or a
  // NOT_HELD member, for which no report ran at all.
  option_type: string | null;
  status: BucketReportStatusEnum;
  message: string | null;
}

/**
 * One day of a scope's chart series: summed cumulative_beginning_value for
 * Bucket 1/2, the account's Orion market value for Bucket 3.
 */
export interface BucketDailyValue {
  date: string;
  // null = Orion couldn't price the account that day (Bucket 3 only).
  value: number | null;
}

/**
 * card_1/2 are the scope's starting and ending value, card_3 their
 * difference, card_4 the bucket's weight-adjusted time-weighted return,
 * card_5 income received, card_6 the account's margin interest.
 *
 * `card_4` is null EXACTLY when `card_4_unavailable` carries a reason, and
 * never otherwise -- the same invariant BucketPerformanceSeries documents
 * below, for the same reason: a scope with no return is withheld rather than
 * flattened to a 0, which in a percentage card reads as "this scope broke
 * even". That is a RESULT, and the backend's point is that it produced none.
 * Three scopes have no figure by design and each says so in its own words
 * (Bucket 3 is calculated at the account level; LONG_VALUE/NET_VALUE are
 * sums across buckets rather than buckets), and a pair bucket the run
 * refused carries the calculation's own sentence.
 *
 * `card_4_unavailable` is OPTIONAL for the same reason performance_series
 * is: the job cache key is versioned on the data and never on the code, so a
 * job COMPLETED before this field shipped is served to the new frontend for
 * the rest of its TTL, carrying `card_4: 0` and no reason at all. Typing it
 * as required would promise a guarantee the runtime does not have.
 *
 * `card_1_date`/`card_2_date` are the days card_1 and card_2 were actually
 * measured on, ISO "YYYY-MM-DD" (operator, task 3730). The backend sends one
 * ONLY where the bucket genuinely started after the period the reader asked
 * for, or genuinely ended before it -- not wherever the date differs from the
 * requested endpoint. A period opening on a weekend or a market holiday is
 * priced on the next session and is NOT a late start: the bucket was there
 * the whole time, so there is nothing to tell the reader and no key is sent.
 * Numbered scopes only: `long_value` and `net_value` span the buckets rather
 * than being one, so no single date describes their figures and neither key
 * is emitted for them.
 *
 * Both are OPTIONAL on exactly the terms `card_4_unavailable` above is, and
 * for the same cache reason stated there.
 *
 * `card_4_by_formula` is that same return measured every way the report
 * knows how, so the Summary gear switches between them with NO re-run
 * (operator, task 3764). One entry per BucketTwrFormula, each holding that
 * formula's own figure and the sentence refusing it -- the same
 * null-exactly-when-a-reason pair `card_4` itself holds, per entry, because
 * the two formulas refuse independently: a bucket absent from the opening
 * snapshot is refused by one and measured by the other, which is the very
 * case the second formula exists to cover.
 *
 * ONLY A PAIR BUCKET HAS TWO ANSWERS. Bucket 3's account chain, Long Value's
 * cross-bucket total and Net Value's balance-adjusted chain are not
 * BucketTwrFormula measurements at all, so each carries its ONE figure under
 * both keys -- the honest shape for a scope the setting cannot move, and
 * what lets the reader's pick mean the same thing on every scope.
 *
 * `card_4` itself is UNCHANGED and still the RUN's formula: the workbook and
 * the Diversify card's aggregate rows go on reading it. OPTIONAL on exactly
 * the terms `card_4_unavailable` is, and for the same cache reason -- a job
 * cached before this shipped carries no such key, and the reader falls back
 * to `card_4`, which shows that run's figure under either pick.
 */
export interface BucketSingleInfoCards {
  card_1: number;
  card_2: number;
  card_3: number;
  card_4: number | null;
  card_4_unavailable?: string | null;
  card_4_by_formula?: Partial<Record<BucketTwrFormula, BucketFormulaReturn>>;
  card_1_date?: string | null;
  card_2_date?: string | null;
  card_5: number;
  card_6: number;
}

/**
 * One formula's answer for a scope: the figure, or the sentence refusing it.
 *
 * `value` is null EXACTLY when `unavailable` carries a reason, the same
 * invariant `card_4`/`card_4_unavailable` hold above and enforced backend
 * side in the one place the card shape is built (build_info_cards).
 */
export interface BucketFormulaReturn {
  value: number | null;
  unavailable?: string | null;
}

/**
 * One day of a performance chart series, as a percentage INDEX rather than
 * money: `total` is where 100% invested at the period's opening stands on
 * this date, and `daily` is that one day's own move.
 *
 * `daily` is null on the first point alone — the baseline the period is
 * measured from has no previous day to have moved against, and a 0 there
 * would claim a flat day the series never had.
 */
export interface BucketPerformancePoint {
  date: string;
  daily: number | null;
  total: number;
}

/**
 * One chart's series. `points` is null EXACTLY when `unavailable` carries a
 * reason, and never otherwise — the same invariant BucketSingleInfoCards
 * documents, for the same reason: a series the backend could not produce is
 * withheld rather than flattened to an empty chart, which reads as a period
 * in which nothing happened. The reason is user-facing prose, shown BENEATH
 * the chart rather than in place of it: every selected series is overlaid on
 * one set of axes, so a refused one no longer occupies a chart of its own
 * that it could take the other lines down with.
 */
export interface BucketPerformanceSeries {
  points: BucketPerformancePoint[] | null;
  unavailable: string | null;
}

/**
 * The chart series behind one pair bucket's section of the performance
 * chart: the bucket's own line — its weight-adjusted time-weighted return,
 * indexed — and one line per member, keyed by symbol.
 *
 * `members` carries EVERY member the run covered, a member whose own report
 * failed included — that one holds the refusal in `unavailable`. So the
 * securities drop-down is built from these keys and stays complete rather
 * than silently dropping the members a reader would go looking for.
 *
 * Bucket 1 and Bucket 2 alone. Bucket 3 has no set at all rather than an
 * empty one: it runs no per-member reports and the account-level series it
 * would need is not calculated, so `performance_series` is simply absent on
 * its entry and the selector reads that as nothing to offer.
 */
export interface BucketPerformanceSeriesSet {
  bucket: BucketPerformanceSeries;
  members: Record<string, BucketPerformanceSeries>;
  /**
   * The bucket's members measured on their STOCK legs alone — their
   * equity price moves and dividends, without the options written against
   * them, each weighted by its share of the bucket's stock at the period's
   * start (operator, task 3576). The chart's "Only Stocks" line.
   *
   * OPTIONAL, and its absence is the whole signal: only Bucket 1 is given
   * one, so the drop-down offers the row exactly where there is a series
   * behind it and never invites a selection nothing could draw. A bucket
   * that HAS one carries it whether it produced points or a refusal —
   * refusing is what a chart says in place of a line, not a reason to hide
   * the control — so `undefined` here means "not offered" and never "could
   * not be measured".
   *
   * It is also optional for the reason `performance_series` itself is: the
   * job cache key is versioned on the data and never on the code, so a job
   * COMPLETED before this field shipped is served to the new frontend for
   * the rest of its TTL with no `stocks` key at all.
   */
  stocks?: BucketPerformanceSeries;
  /**
   * The bucket's OWN line measured every way the report knows how, keyed by
   * BucketTwrFormula (operator, task 3764) -- what lets the Summary gear
   * re-draw the chart from the payload already on screen instead of
   * re-running the report.
   *
   * ONLY THE BUCKET LINE IS CARRIED TWICE: the members and the stock line
   * are the same points under either formula, because the formula weights
   * the BUCKET and not the securities in it.
   *
   * Each line's last point is 100 plus THAT formula's own card_4
   * (`card_4_by_formula`), which is the same knot `bucket` ties once -- so a
   * switched chart still ends where the switched card beside it says.
   *
   * OPTIONAL on exactly the terms `stocks` above is, and for the same cache
   * reason: a job COMPLETED before this shipped carries no such key and the
   * reader falls back to `bucket`, drawing that run's line under either pick.
   */
  bucket_by_formula?: Partial<
    Record<BucketTwrFormula, BucketPerformanceSeries>
  >;
}

export interface BucketTotalsEntry {
  daily_values: BucketDailyValue[];
  single_info_cards: BucketSingleInfoCards;
  // OPTIONAL because the cache key is versioned on the data and never on the
  // code: a job COMPLETED before this field shipped is served to the new
  // frontend for the rest of the job TTL, carrying no performance_series at
  // all. Typing it as required would promise a guarantee the runtime does not
  // have, and the next reader would subscript it unguarded.
  performance_series?: BucketPerformanceSeriesSet;
  /**
   * Bucket 1/2 only: each member's contribution to the bucket TWR in
   * percentage points (they sum to card_4) and its day-one weight.
   *
   * Optional on the same terms as `performance_series` above: a job cached
   * before these shipped carries neither key.
   */
  contributions?: Record<string, number>;
  start_weights?: Record<string, number>;
}

/** The last-day position sums and the linked TWR only Bucket 3 carries. */
export interface Bucket3Totals extends BucketTotalsEntry {
  /**
   * Bucket 3's own flow-adjusted linked return over the period
   * (bucket_performance.calculate_bucket3_performance), in percent.
   *
   * Null when the run could not measure it -- an account with no priced day,
   * or none Orion valued above 0. The REASON lives on this scope's
   * `single_info_cards.card_4_unavailable`, which is the one home for every
   * refused return on this payload; this field never carries a 0 in place of
   * one, since 0 on a return reads as "the bucket broke even".
   *
   * Optional on the same terms as `performance_series`: a job cached before
   * this field shipped carries no such key.
   */
  twr?: number | null;
  market_value: number | null;
  cost_basis: number | null;
  unrealized_gain_loss: number | null;
  positions_as_of: string | null;
  end_date: string;
}

/**
 * Keyed as the summary switch selects. A summary with no successful data
 * (an empty bucket, a not-requested or failed Bucket 3) is absent.
 */
export type BucketTotals = {
  [BucketSummaryEnum.BUCKET_1]?: BucketTotalsEntry;
  [BucketSummaryEnum.BUCKET_2]?: BucketTotalsEntry;
  [BucketSummaryEnum.BUCKET_3]?: Bucket3Totals;
  [BucketSummaryEnum.LONG_VALUE]?: BucketTotalsEntry;
  [BucketSummaryEnum.NET_VALUE]?: BucketTotalsEntry;
};

/**
 * Which way a Keep card participation rate measures: the shared sign of the
 * stock's return and its return with options (backend
 * bucket_totals._participation_rate).
 */
export type ParticipationDirection = "upside" | "downside";

/**
 * One "Keep" card option: a single asset's values, or their sum.
 *
 * `participation_rate` is the strategy's return on the stock as a percentage
 * of the stock's own return, measured only when the two moved the same way:
 * `participation_direction` says which -- "upside" (both rose, the share of
 * the rise captured) or "downside" (both fell, the share of the fall taken)
 * -- and is null beside a refusal. The rate is null
 * EXACTLY when `participation_rate_unavailable` carries the sentence saying
 * why, the same pairing `card_4`/`card_4_unavailable` uses and for the same
 * reason: a percentage flattened to 0 reads as "the strategy captured none of
 * the rise", which is a result where the truth is that there is no result.
 * The `total` option's is ALWAYS the refusal -- the rate compares one stock's
 * two returns, so a portfolio sum of it means nothing.
 *
 * All three are optional because a job that COMPLETED before these fields shipped is
 * served from cache: such a payload carries `participation_rate: 0` and no
 * reason at all, and reads exactly as it did before.
 */
export interface KeepCardValues {
  shares_start: number;
  shares_end: number;
  value_start: number;
  value_end: number;
  dividends: number;
  participation_rate?: number | null;
  participation_rate_unavailable?: string | null;
  participation_direction?: ParticipationDirection | null;
  basis: number;
  tax_savings: number;
}

/** The "Keep" themed card, from Bucket 1 only — it does not follow the page's bucket switch. */
export interface KeepThemedCardValues {
  total: KeepCardValues;
  assets: Record<string, KeepCardValues>;
}

/**
 * The "Protect" themed card, from Bucket 1 only. collar_pl is the
 * period's total option cashflows; max_stock_drop holds each member's
 * max drop percent, keyed by symbol, and main_symbol is the asset the
 * dropdown should default to (null when no main member has data);
 * oea_drop is the whole portfolio's max drop percent — the summed
 * Bucket 1/2/3 daily values (null when no bucket has values);
 * available_margin_balance is reserved (0 for now).
 */
export interface ProtectThemedCardValues {
  max_stock_drop: Record<string, number>;
  main_symbol: string | null;
  oea_drop: number | null;
  collar_pl: number;
  available_margin_balance: number;
}

/**
 * The "Harvest" themed card. unrealized_gain_loss is its one CARD-LEVEL figure
 * — the PORTFOLIO's total unrealized gain/loss: every lot on the report
 * account, which carries Buckets 1 and 2, plus every lot on the named
 * Bucket 3's account when that is a different one, each account taken on its
 * own latest position snapshot on or before the report's end date (task
 * 3644). Account-wide within each of them, and so unaffected by the page's
 * bucket switch. It is the same figure the
 * workbook's "Unrealized G/L" sheet totals, and null when the run could not
 * produce it (no snapshot, or no position on that date carrying a figure) —
 * in which case the reader's stored value stays, as oea_drop's does.
 *
 * Shares Sold and Sellable Today are calculated too, but PER STOCK rather
 * than as figures of this card — `stocks` below. The card's other three
 * rows (realized losses, losses used, pool remaining) are not part of this
 * payload.
 */
export interface HarvestThemedCardValues {
  unrealized_gain_loss: number | null;
  // Why the run could not produce unrealized_gain_loss, in user-facing prose —
  // set EXACTLY when it is null, and null when the figure is there. The
  // card renders the pair as "N/A" behind an info icon rather than leaving
  // the reader's stored number standing as though this run had produced it.
  unavailable: string | null;
  // Every Bucket 3 stock, keyed by symbol and already alphabetical (the
  // bucket's selection is ordered by symbol) — the options the select
  // between the Shares Sold and Sellable Today rows offers, each carrying
  // that stock's own two figures. The run computes all of them, so
  // switching the pick costs no request; the Keep card's `assets` is the
  // same arrangement for the same reason.
  //
  // `{}` — present and empty — when the run named no Bucket 3, or the
  // bucket has no members: "there are no stocks to pick" is an answer.
  // OPTIONAL because a job result cached before these rows shipped carries
  // no such key at all, and reports stay in the cache for 12 h across a
  // deploy — which is the absence the frontend's `?? null` reads, and it
  // leaves the two rows exactly as they were.
  stocks?: Record<string, HarvestStockCounts>;
  // The day every `sellable_today` in `stocks` was counted on: the
  // account's latest position snapshot, which is NOT the period's end and
  // can be several days behind a row labelled "Today". A companion to those
  // counts on the same terms as the gain's own coverage — it is the only
  // thing that lets a reader tell a current count from a stale one. ISO
  // date, null when there was no snapshot to read (and then every stock's
  // `sellable_today` is null carrying that reason).
  sellable_as_of?: string | null;
}

/**
 * One Bucket 3 stock's Harvest figures.
 *
 * `shares_sold` is shares of it sold out of the Bucket 3 account over the
 * report PERIOD; `sellable_today` is shares of it held there NOW, on
 * `sellable_as_of`. Two different windows on purpose — the backend's
 * bucket_3_share_counts says why.
 *
 * Each is null exactly when `unavailable` carries the reason under the same
 * key, the rule every refused figure in this payload follows: a stock Orion
 * imported without a product id cannot have its sales looked up, and an
 * account with no position snapshot has no holding to count. Neither is a
 * 0, which on these two rows would read as "sold nothing" and "nothing left
 * to sell".
 */
export interface HarvestStockCounts {
  shares_sold: number | null;
  sellable_today: number | null;
  unavailable: Partial<Record<"shares_sold" | "sellable_today", string>>;
}

/**
 * The Diversify card's two calculated rows. Each carries its own reason key,
 * because one `unavailable` could not say which of them it explained:
 * Overall Diversified Performance keeps the unsuffixed key it shipped alone
 * with, and every figure added after it names itself.
 */
export interface DiversifyThemedCardValues {
  /**
   * BUCKET 2 AND BUCKET 3's aggregate weight-adjusted return: each of
   * those buckets' own return over the period scaled by its share of the
   * two's opening value, summed. Bucket 1 is excluded from the terms and
   * its weight redistributed over the two that remain (task 3573), so
   * this is no longer the whole portfolio's return -- that figure travels
   * beside it as `portfolio_aggregate_return`.
   *
   * Null when a bucket the run produced could not take part, BUCKET 1
   * INCLUDED: narrowing the arithmetic did not narrow the evidence, so a
   * run that could not measure Bucket 1 still refuses this row rather
   * than reporting over the buckets that happened to work.
   */
  overall_diversified_performance: number | null;
  /**
   * Why the run could not produce OVERALL DIVERSIFIED PERFORMANCE, in
   * user-facing prose -- set EXACTLY when that figure is null, and null
   * when it is there. It is the refusing BUCKET's own sentence, so a
   * reader who hovers this row gets the same words that bucket's own
   * summary card gives her.
   */
  unavailable: string | null;
  /**
   * The WHOLE portfolio's aggregate weight-adjusted return -- the same
   * arithmetic over every bucket the run produced, which the row above
   * was until task 3573 narrowed it to Buckets 2 and 3. It is what the
   * Long Value summary card's card_4 is built from, and it is carried
   * separately precisely because the two are now different numbers.
   */
  portfolio_aggregate_return: number | null;
  /**
   * Why the run could not produce PORTFOLIO AGGREGATE RETURN, in
   * user-facing prose -- set EXACTLY when that figure is null. It is the
   * refusing bucket's own sentence, the same one the row above carries in
   * every case but a run that produced Bucket 1 alone.
   */
  portfolio_aggregate_return_unavailable: string | null;
  /**
   * The Tax-Loss Harvest Sleeve's performance over the period. The sleeve
   * IS Bucket 3 (operator, task 2822), so this is Bucket 3's own
   * performance figure read off its summary card -- the same number that
   * bucket's card_4 shows and the same one the aggregate above weights,
   * so the two rows reconcile by hand.
   *
   * Null when the run produced no Bucket 3, or produced one whose return
   * it refused -- independent of `unavailable` above, which is the
   * aggregate's: the aggregate is refused whenever ANY bucket cannot take
   * part, so this row can carry a figure while the row above it carries a
   * sentence.
   */
  tax_loss_harvest_sleeve: number | null;
  /**
   * Why the run could not produce the TAX-LOSS HARVEST SLEEVE -- set
   * EXACTLY when that figure is null. It is Bucket 3's OWN sentence
   * wherever the bucket had one, so a reader who hovers this row gets the
   * same words Bucket 3's summary card gives her.
   */
  tax_loss_harvest_sleeve_unavailable: string | null;
  /**
   * Current Concentration: the percent of the whole portfolio still
   * sitting in Bucket 1's stock -- its stock value at the end of the
   * period over every bucket's combined GROSS value at the end of the
   * period (operator, task 3348; build_diversify_card holds the whole
   * definition and the reasons behind each half).
   *
   * NOT clamped to 100: a short position can net against the portfolio's
   * total without netting against Bucket 1's stock, and the row shows what
   * was measured. The donut is what clamps.
   *
   * Beginning Concentration is absent on purpose -- it is 100% by
   * definition and the reader's to edit (task 3141), so no run may
   * overwrite it.
   */
  current_concentration: number | null;
  /**
   * Why the run could not produce CURRENT CONCENTRATION -- set EXACTLY
   * when that figure is null. Independent of `unavailable` above: one
   * figure being refused leaves the other standing, so the row renders
   * "N/A" behind an info icon and the donut draws nothing, while the
   * aggregate return beside them is unaffected.
   */
  current_concentration_unavailable: string | null;
  /**
   * Current Concentration measured for ONE Bucket 1 stock at a time,
   * keyed by symbol -- what the card's stock dropdown shows when the
   * reader picks a symbol rather than All Stocks (operator, task 3508).
   *
   * The same ratio as `current_concentration` with a narrower numerator:
   * this stock's market value at the end of the period over the SAME
   * portfolio total, so the entries here are shares of the figure above
   * and sum to it.
   *
   * An EMPTY map is a run that produced no Bucket 1 asset -- there is no
   * stock to select, which is not a figure withheld -- so the dropdown
   * lists nothing and no row is refused. Null is the refusal, and then
   * the reason below carries the sentence.
   */
  current_concentration_by_symbol: Record<string, number> | null;
  /**
   * Why the run could not measure CURRENT CONCENTRATION PER STOCK -- set
   * EXACTLY when the map above is null. The refusal is the denominator's,
   * so it is the same sentence `current_concentration_unavailable`
   * carries: a run with no portfolio total can measure no stock's share
   * of it.
   */
  current_concentration_by_symbol_unavailable: string | null;
  /**
   * Beginning Concentration measured for ONE Bucket 1 stock at a time,
   * keyed by symbol (operator, task 3508): that stock's market value at
   * the START of the period over Bucket 1's own stock market value at the
   * start of the period.
   *
   * The entries sum to 100, which is the All Stocks baseline -- and that
   * baseline is deliberately NOT an entry here. It is 100% by definition
   * and the reader's to edit (task 3141), so no run may send it and
   * useBeginningConcentration stays its single home.
   *
   * Empty and null mean what they mean on the map above.
   */
  beginning_concentration_by_symbol: Record<string, number> | null;
  /**
   * Why the run could not measure BEGINNING CONCENTRATION PER STOCK --
   * set EXACTLY when the map above is null, and on its OWN terms rather
   * than the portfolio total's: a run can price the whole portfolio today
   * and still have opened Bucket 1 at nothing.
   */
  beginning_concentration_by_symbol_unavailable: string | null;
  /**
   * Holdings: how many DISTINCT securities the report's Bucket 2 and
   * Bucket 3 held on the last position snapshot of the period -- Bucket 2's
   * members plus the option contracts written on them, and Bucket 3's
   * members, with a security both buckets hold counted once (operator, task
   * 3349; resolve_holdings_count holds the whole definition).
   *
   * Read from the POSITIONS rather than from the bucket member lists, which
   * carry no dates and so describe only today: a member sold before the end
   * date held nothing then and is not counted.
   *
   * A count of 0 is a RESULT, not an absence -- a client holding nothing in
   * either bucket is an answer -- so it arrives with no reason beside it and
   * the row must apply it rather than test it for truthiness.
   */
  holdings: number | null;
  /**
   * Why the run could not produce HOLDINGS -- set EXACTLY when that figure
   * is null, and independent of the two reasons above. Either the report
   * named no Bucket 3 (the count is a two-bucket sum, so it is refused whole
   * rather than reported as the half it could reach) or a bucket's account
   * has no snapshot on or before the end date; that sentence names WHICH
   * bucket, since the two may be on different accounts.
   */
  holdings_unavailable: string | null;
}

/**
 * The Drawdown Comparison chart's y-axis bounds, stored per protect asset
 * (useMaxDropLimits): `maxDrop` is the positive end of the axis and
 * `negativeLimit` the negative one. Derived from the asset's own two drops
 * until the operator types over them in the edit modal.
 */
export interface MaxDropLimits {
  maxDrop: number;
  negativeLimit: number;
}

/** The rest of diversify/harvest lands as siblings in follow-ups. */
export interface BucketThemedCards {
  keep?: KeepThemedCardValues;
  protect?: ProtectThemedCardValues;
  diversify?: DiversifyThemedCardValues;
  harvest?: HarvestThemedCardValues;
}

export interface BucketReportResponse {
  excel_url: string;
  reports: BucketPairReportStatus[];
  // These are optional only because a job result cached before the field
  // shipped won't carry it; fresh responses always do.
  bucket_totals?: BucketTotals;
  themed_cards?: BucketThemedCards;
  // Which arithmetic produced this result's Bucket 1/2 figures, echoed by
  // the backend. The Summary gear reads it back rather than trusting its own
  // pick: a run that failed leaves the cards showing the PREVIOUS formula's
  // numbers, and a radio that had already moved would be naming a formula
  // nothing on screen was calculated by.
  twr_formula?: BucketTwrFormula;
  // Which option-pairing formula (task 3869) produced this result.
  option_pairing_v2?: boolean;
}

export interface ReportJob<TResult> {
  key: string;
  status: ReportJobStatus;
  result: TResult | null;
  status_message: string | null;
  // Omitted by the backend on a NOT_FOUND status response.
  started_at?: string;
}

export interface NewReportSingleInfoCards {
  card_1: number;
  card_2: number;
  card_3: number;
  card_4: number;
  card_5: number;
  card_6: number;
}

export interface NewReportThemedCards {
  keep_your_shares: {
    starting_shares: number;
    current_shares: number;
    stock_price_start: number;
    stock_price_now: number;
    stock_only_return: number;
    upside_participation: number;
  };
  generate_income: {
    option_income_period: number;
    dividend_income_period: number;
    cumulative_income: number;
    avg_annualized_yield: number;
    current_option_buyback: number;
    net_option_income: number;
  };
  harvest_tax_benefits: {
    net_realized_tlh_losses: number;
    losses_used: number;
    losses_remaining: number;
    shares_sold: number;
    sellable_today: number;
  };
}

/** Strategy report themed sections; mirrors keys returned by the new-report API for report_type strategy. */
export interface NewReportStrategyThemedCards {
  keep: {
    shares_start: number;
    shares_end: number;
    value_start: number;
    value_end: number;
    dividends: number;
    participation_rate: number;
    basis: number;
    tax_savings: number;
  };
  protect: {
    max_stock_drop: number;
    collar_pl: number;
    available_margin_balance: number;
  };
  diversify: {
    overall_diversified_performance: number;
    tax_loss_harvest_sleeve: number;
    beginning_concentration: number;
    current_concentration: number;
    holdings: number;
  };
  harvest: {
    realized_losses: number;
    losses_used: number;
    pool_remaining: number;
    unrealized_gains: number;
    shares_sold: number;
    sellable_today: number;
  };
}

export interface NewReportResponse {
  status: "success";
  data: {
    single_info_cards: NewReportSingleInfoCards;
    graph_or_table_data: ReportChartDataPoint[];
    themed_cards?: NewReportThemedCards | NewReportStrategyThemedCards;
    excel_url?: string;
  };
}

/** PDF report request: matches backend PDFReportSerializer */
export type ReportType = "stock" | "strategy";

export interface SelectedAsset {
  id: number;
  symbol: string;
  name: string;
}

export interface PDFReportSettingsInfo {
  client_name: string;
  client_id?: number | null;
  account_id?: number | null;
  account: string;
  period: [string, string];
  global_cumulative_period?: [string, string];
  symbol: string;
  strategy_id?: number | null;
  orion_account_id?: number | null;
  // The exact Bucket 3 the report should use — a client can have several
  // accounts with a Bucket 3 each (or none), and it may belong to a
  // different account than the Bucket 1/2 one.
  bucket_3_id?: number | null;
}

export interface PDFReportTableColumnRow {
  id: number;
  Period: string;
  "Stock Price": number;
  "Option Income": number;
  "Dividend Income": number;
  "Total Income": number;
  Comment: string;
}

export interface ReportChartDataPoint {
  date: string;
  option_total: number;
  stock_total: number;
}

export enum PDFReportValueType {
  NUMBER = "number",
  CURRENCY = "currency",
  PERCENTAGE = "percentage",
  SHARES = "shares",
}

export enum PDFReportChartType {
  BAR = "bar",
  DONUT = "donut",
}

export interface PDFReportClientInfoItem {
  label: string;
  value: string | number;
  type: PDFReportValueType;
  description: string;
}

export interface PDFReportValueItem {
  label: string;
  // A number for a figure, or one of the report's seed strings ("No Report",
  // "NOT IMPLEMENTED") for a field that has none. The seeds print verbatim so
  // the exported PDF says the same thing the screen does rather than a $0.00
  // nobody calculated; the backend renders a non-numeric value as-is.
  value: string | number;
  type: PDFReportValueType;
}

export interface PDFReportAddictionalComponent {
  type: PDFReportChartType;
  title: string;
  limit: number;
  negativeLimit?: number;
  data: PDFReportValueItem[];
}

export interface PDFReportThemeInfoItem {
  label: string;
  description: string;
  values: PDFReportValueItem[];
  addictional_component?: PDFReportAddictionalComponent | null;
}

/**
 * One line of the Strategy Report's performance chart as the PDF draws it:
 * what the on-screen legend calls it, its stroke, and its total at each date
 * of the chart's shared axis (null on a date it has no priced day for).
 */
export interface PDFReportPerformanceLine {
  title: string;
  color: string;
  /** SVG `stroke-dasharray`; "" for a solid line. */
  dash: string;
  totals: (number | null)[];
}

/** The performance chart exactly as it is drawn on screen at export time. */
export interface PDFReportPerformanceChart {
  dates: string[];
  lines: PDFReportPerformanceLine[];
}

export interface PDFReportRequest {
  type: ReportType;
  settings_info?: PDFReportSettingsInfo;
  table_columns?: PDFReportTableColumnRow[];
  performance_chart?: PDFReportPerformanceChart;
  client_info: PDFReportClientInfoItem[];
  theme_info?: PDFReportThemeInfoItem[];
}

// ALERT DOMAIN TYPES AND HELPERS
export type AlertLeg = {
  action?: string;
  expiration_date?: string;
  strike_price?: number | string | null;
};

export type AlertPayload = {
  contract?: {
    legs?: AlertLeg[];
  };
  [key: string]: unknown;
};

export type AlertType = {
  id: number;
  type?: string | null;
  status?: string | null;
  seen?: boolean;
  time_sent?: string | null;
  order_id?: string | null;
  symbol?: string | null;
  contract_type?: "C" | "P" | string | null;
  net_avg_price?: number | string | null;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  legs?: AlertLeg[];
  payload?: AlertPayload | string | null;
};

export const asText = (value: unknown) =>
  value === null || value === undefined ? "—" : String(value);

export const formatMoney = (value: unknown, decimals = 4) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(decimals)}`;
};

export const contractLabel = (contractType?: string | null) => {
  if (contractType === "C") return "Call";
  if (contractType === "P") return "Put";
  return asText(contractType ?? "");
};

export enum PerformanceReportTypeEnum {
  STOCK = "stock",
  STRATEGY = "strategy",
}

export enum AdjustmentTypeEnum {
  INCEPTION = "INCEPTION",
  BUY = "BUY",
  SELL = "SELL",
  REMOVED = "REMOVED",
  MERGE_IN = "MERGE_IN",
  JOURNAL_IN = "JOURNAL_IN",
  JOURNAL_OUT = "JOURNAL_OUT",
  MAX_TRADE_AMOUNT = "MAX_TRADE_AMOUNT",
}

export enum BalanceTypeEnum {
  TRADE_AMOUNT = "TRADE_AMOUNT",
  PORTFOLIO = "PORTFOLIO",
}

export interface AdjustmentRow {
  id: number;
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: number;
  unit_balance: number;
  balance_type: BalanceTypeEnum;
  ignore: boolean;
}

export enum OptionTypeEnum {
  CALL = "C",
  PUT = "P",
  CALL_AND_PUT = "CP",
}

export interface AccountHistoryRecord {
  id: number;
  account: number;
  asset: number | null;
  option_type: OptionTypeEnum | null;
  strategy: number | null;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  unit_balance_after_adjustment: number;
  balance_type: string;
  ignore: boolean;
  created_on?: string;
  created_by?: number | null;
  updated_on?: string;
  updated_by?: number | null;
}

export interface AccountHistoryQueryParams {
  account_id: number;
  asset_id: number;
  option_type?: OptionTypeEnum;
  ordering?: string;
  balance_type?: string;
  adjustment_type?: string;
}

export interface CreateAccountHistoryPayload {
  account: number;
  asset: number;
  option_type?: OptionTypeEnum;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  balance_type?: string;
  ignore?: boolean;
}

// Map a Strategy's ``call_or_put`` flag to the OptionTypeEnum letter used in
// the asset-based AccountHistory API. Mirrors the backend enum:
//   call_or_put === true  -> PUT
//   call_or_put === false -> CALL
export const optionTypeFromCallOrPut = (
  callOrPut: boolean | null | undefined,
): OptionTypeEnum => (callOrPut ? OptionTypeEnum.PUT : OptionTypeEnum.CALL);

export interface SingleValueCardProps {
  label: string;
  // Same union as ReportValueCardProps.value, and for the same reason: every
  // one of these figures comes from useStock/StrategyReportValue, which is
  // localStorage-backed and hands back `number | string`. Declaring `number`
  // here did not make it one -- it made all twelve render sites launder the
  // union through `as number`, and a "" that reached the percentage formatter
  // that way crashed the app.
  //
  // `null` is in the union on the same terms, and it is the second time that
  // lesson has had to be learnt here: the storage hooks' SETTERS validate
  // nothing, so whatever a report payload carries reaches these cards
  // unexamined, and a cached result built by an earlier backend carries a
  // literal null where BucketSingleInfoCards promises a number. Narrowing
  // this to `number | string` did not make the runtime obey it; it only hid
  // the case from the card, which then crashed on it. The cards read it as a
  // figure that was not produced and render N/A.
  value: number | string | null;
  description: string;
  type: SingleValueCardType;
  color: SingleValueCardColor;
  onValueChange: (value: number) => void;
  // Same contract as ReportValueCardProps.unavailableReason: set EXACTLY when
  // the backend produced no figure and said why. It is not the only thing
  // marking a figure as missing -- the reason lives in component state while
  // the value it explains is persisted, so a reload or a settings change
  // leaves the value behind without it. The card treats an unusable value as
  // missing whether or not a reason survived to accompany it.
  unavailableReason?: string;
  // Same contract as ReportValueCardProps.note: set when the backend DID
  // produce this figure but qualified it, so the figure keeps its own weight
  // and colour and the sentence hangs off an info icon beside it.
  note?: string;
  // The tile is not part of the answer the page is currently showing, so it
  // recedes: the whole card is dimmed and its figure stops accepting an
  // override (operator, task 3580). Orthogonal to the two channels above and
  // deliberately so -- those say something about the FIGURE (there is none /
  // here is what qualifies it), where this says something about the tile's
  // relevance to the scope on screen, and the figure it dims is a real one
  // that is still correct for the period.
  //
  // Set today only by the Strategy Report's Summary section, whose two
  // scope-independent tiles -- Income Received and Margin Interest -- take it
  // on Net Value; summaryTileDimmed is the single home for which scope dims
  // which tiles, and for why the return tile stopped being one of them
  // (operator, task 3637).
  dimmed?: boolean;
  // A dimmed tile that still takes an override (operator, task 3781): Net
  // Value's withheld Income Received and Margin Interest recede until the
  // reader types their own figure, then stop receding. Ignored on an
  // undimmed tile, which takes an override anyway.
  overridable?: boolean;
  // What an emptied box means -- EditableTextField.onClear, passed through.
  onClear?: () => void;
}
export interface MultiValuesCardProps {
  title: string;
  values: ReportValueCardProps[];
  description: string;
  additionalComponents: React.ReactNode | null;
  // Rendered in the title bar, opposite the title (e.g. the Keep card's
  // asset select). Display-only — the PDF payload ignores it.
  headerAction?: React.ReactNode;
}

export interface ReportValueCardProps {
  label: string;
  // Nullable on the same terms as SingleValueCardProps.value above, which
  // carries why. These rows take their figures from the same payload through
  // the same unvalidated setters, so a null reaches them the same way.
  value: number | string | null;
  type: SingleValueCardType;
  onValueChange: (value: number | string) => void;
  fullWidthRow?: boolean;
  color?: SingleValueCardColor;
  // Rendered right next to the value field (e.g. the Keep card's asset
  // select beside Shares End, the Protect card's stock select beside Max
  // Stock Drop). Display-only — toPdfValues ignores it.
  action?: React.ReactNode;
  // Rendered before the label (e.g. the Keep card's tax settings gear on
  // Tax Savings). Display-only — toPdfValues ignores it.
  labelAction?: React.ReactNode;
  // Rendered INSTEAD of the value field, for a row that is a control
  // rather than a figure (the Harvest card's Bucket 3 stock select, which
  // the operator asked to sit between the two rows it drives). A row with
  // one carries no figure at all, so it is absent from the PDF export —
  // toPdfValues drops it, the same way it drops a row whose figure the
  // backend refused.
  control?: React.ReactNode;
  // Label the PDF export uses instead of `label`, for a row whose `action`
  // carries something the reader needs and the export cannot show (the OEA
  // Max Drop's bucket list). Omitted rows export `label` unchanged.
  pdfLabel?: string;
  // Why the backend could not produce this row's figure, in user-facing
  // prose. It does not replace the empty-string `value`, it qualifies it:
  // `value === ""` alone still means "no figure" and still renders BLANK,
  // which is the Keep card's Participation Rate and is unchanged. With
  // this ALSO set the display becomes the literal text "N/A" wrapped in a
  // tooltip carrying the reason, so a figure that could not be produced
  // reads as absent-and-explained rather than as a silent gap. Both forms
  // stay editable, and both leave the export alone — toPdfValues drops a
  // row for EITHER, and needs both tests: a row carrying a reason may hold
  // a real stored number underneath its N/A (so that a refusal cannot
  // destroy a hand-entered figure), and exporting that would print it as
  // this run's result.
  unavailableReason?: string;
  // An advisory sentence about a figure the backend DID produce, in
  // user-facing prose. The mirror image of `unavailableReason` above and not
  // a variant of it: that one explains a number that is absent, this one
  // qualifies a number that is present -- a Bucket 3 figure measured from
  // the bucket's creation date rather than the period's start, the snapshot
  // day a bucket total actually landed on. The row therefore renders the
  // figure NORMALLY -- full figure weight, its own colour, no "N/A" -- with
  // the sentence behind the same info icon a reason uses. Muting it the way
  // an N/A is muted would tell the reader the figure is missing when it is
  // not.
  //
  // Display-only, exactly like `action`: toPdfValues ignores it, and a row
  // carrying one still exports its figure under its plain label. The two can
  // arrive together -- the backend refusing a figure and having something to
  // add about the refusal -- and the tooltip then reads as two sentences,
  // the reason first.
  note?: string;
  // What an EMPTIED value field means, for a row that has an answer. Omitted
  // — which is every row but one — clearing the box writes nothing and the
  // figure underneath stays, which is what protects a persisted figure from a
  // stray keystroke.
  //
  // The Protect card's Available Margin Balance is the row that supplies it.
  // Its default is a STATE rather than a number (`value` is null until an
  // operator types one, so the row reads N/A and the export omits it), and a
  // state nobody can get back to is a trap: a figure typed by mistake would
  // keep printing in the client's PDF until the page was reloaded. Clearing
  // the box is the way back, and this is how the row is told.
  //
  // EditableTextField fires it for a blank box only, never for text that
  // merely fails to parse — that one carries why.
  onClear?: () => void;
}

export interface ReportThemedCardConfig {
  label: string;
  description: string;
  /** The rows as the SCREEN shows them. */
  values: ReportValueCardProps[];
  /**
   * The rows as the client's PDF prints them, when the two differ.
   * Defaults to `values`, which is the case for every card whose screen
   * rows say exactly what the export should.
   *
   * The Diversify card is the one that differs (operator, task 3508): its
   * stock switch is a SCREEN-SIDE LENS, so the export keeps printing the
   * whole-of-Bucket-1 concentrations under their plain labels whatever is
   * picked on screen. Without the split a figure measured for one stock
   * would print in a client's copy labelled as the whole bucket -- beside
   * a donut still drawn from the all-stocks figure, so the paper would
   * disagree with itself.
   */
  pdfValues?: ReportValueCardProps[];
  addictional_component: PDFReportAddictionalComponent | null;
  headerAction?: React.ReactNode;
}

export interface ReportPdfResponse {
  download_url?: string;
  [key: string]: string[] | unknown;
}

export interface OrionTransactionParams {
  start_date: string;
  end_date: string;
  strategy_id?: number;
  asset_ids?: number[];
  orion_account_id: number;
  report_type: ReportType;
  transaction_type_ids?: number[];
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
}

export interface OrionTransaction {
  id: number;
  status: string;
  transaction_type_id: number;
  transaction_date: string;
  settlement_date: string;
  transaction_amount: number;
  units_amount: number;
  nav_price: number;
  ticker: string;
  transaction_type: string;
  unit_balance: number;
}

// The row shape of GET /trading/transactions/, the Transactions page's own
// endpoint. Deliberately separate from OrionTransaction above rather than a
// widening of it: that type is the report flow's transaction, fetched with the
// report's own parameters and never null in the fields this page has to render
// as N/A. One type covering both would be wrong for each.
export interface TransactionRecord {
  id: number;
  transaction_date: string | null;
  settlement_date: string | null;
  // Human-readable, and null when the transaction has no matching account.
  account_number: string | null;
  ticker: string | null;
  transaction_type: string | null;
  units_amount: number;
  nav_price: number;
  transaction_amount: number;
  unit_balance: number;
  status: string | null;
}

export type ReportTabT = "stock" | "strategy";

// The two tables of the Securities page's Security Master, as the `kind` the
// /trading/securities/ endpoint takes.
export type SecurityKind = "stock" | "option";

// A Security Master row: every stored asset column. The five classification
// FKs arrive as their names, and are null when the asset has none. `id` is the
// grid's row key only; the page never shows it.
export interface Security {
  id: number;
  symbol: string | null;
  name: string;
  asset_category: string | null;
  asset_class: string | null;
  risk_category: string | null;
  product_type: string | null;
  product_sub_type: string | null;
  orion_product_id: number | null;
  maturity_date: string | null;
  // Decimal string, as imported from Orion; "0.0000" when never priced.
  last_price: string;
  created_on: string;
  // Integer user ids, null when the row was written by an import.
  created_by: number | null;
  updated_on: string | null;
  updated_by: number | null;
}
